import Phaser from 'phaser';
import { WORLD_HEIGHT, WORLD_WIDTH, isTestMode } from '../config';
import { GameScene } from '../render/GameScene';
import { DEFAULT_SCENARIO } from '../scenarios';
import { buildLiveScenario } from '../scenarios/live';
import { getTrack } from '../tracks';
import { VEHICLES } from '../sim/vehicles';
import { GameHarness } from './harness';
import { setupMenu, type MenuChoice } from './menu';
import { installTestHook } from './testApi';

/**
 * Choix initial du jeu réel : `?mode=`/`?track=`/`?vehicle=` (deep-link) sinon le défaut. On VALIDE
 * contre les registres pour que le choix corresponde toujours à une option du menu (sinon désync).
 */
function initialChoice(): MenuChoice {
  const p = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;
  const track = p?.get('track');
  const vehicle = p?.get('vehicle');
  return {
    mode: p?.get('mode') === 'race' ? 'race' : 'free',
    track: track && getTrack(track) ? track : 'stadium',
    vehicle: vehicle && VEHICLES[vehicle] ? vehicle : 'car',
  };
}

export function bootstrap(): void {
  const harness = new GameHarness(DEFAULT_SCENARIO);
  const test = isTestMode();
  const choice = initialChoice();

  // En test : on garde un monde neutre, le test charge son scénario. En jeu : libre ou course.
  harness.reset(test ? DEFAULT_SCENARIO : buildLiveScenario(choice));
  if (test) {
    harness.manual = true;
    harness.setDebug({ help: false }); // pas de légende dans les screenshots de référence
  }

  const scene = new GameScene(harness, { follow: !test });
  const game = new Phaser.Game({
    type: Phaser.AUTO,
    width: WORLD_WIDTH,
    height: WORLD_HEIGHT,
    parent: 'app',
    backgroundColor: '#1b1b1b',
    scene: [scene],
    fps: { target: 60 },
    render: { antialias: true },
  });

  if (test) {
    installTestHook(harness, game);
  } else {
    setupKeyboard(harness);
    setupMenu(harness, choice);
  }
}

/** Mappe clavier (flèches / WASD / espace) sur les inputs de la voiture 0. */
function setupKeyboard(harness: GameHarness): void {
  const keys = { up: false, down: false, left: false, right: false, hand: false };

  const apply = (): void => {
    const input = harness.currentInputs[0];
    if (!input) return;
    input.throttle = keys.up ? 1 : 0;
    input.brake = keys.down ? 1 : 0;
    input.steer = (keys.right ? 1 : 0) - (keys.left ? 1 : 0);
    input.handbrake = keys.hand;
  };

  const set = (code: string, down: boolean): boolean => {
    switch (code) {
      case 'ArrowUp':
      case 'KeyW':
        keys.up = down;
        return true;
      case 'ArrowDown':
      case 'KeyS':
        keys.down = down;
        return true;
      case 'ArrowLeft':
      case 'KeyA':
        keys.left = down;
        return true;
      case 'ArrowRight':
      case 'KeyD':
        keys.right = down;
        return true;
      case 'Space':
        keys.hand = down;
        return true;
      default:
        return false;
    }
  };

  window.addEventListener('keydown', (e) => {
    if (set(e.code, true)) {
      e.preventDefault();
      apply();
      return;
    }
    if (toggleDebug(harness, e.code)) e.preventDefault();
  });
  window.addEventListener('keyup', (e) => {
    if (set(e.code, false)) {
      e.preventDefault();
      apply();
    }
  });
}

/** Touches de debug (jeu réel) : bascule les overlays. Renvoie true si la touche a été gérée. */
function toggleDebug(harness: GameHarness, code: string): boolean {
  const d = harness.debug;
  switch (code) {
    case 'KeyC':
      harness.setDebug({ collisionBodies: !d.collisionBodies });
      return true;
    case 'KeyV':
      harness.setDebug({ velocityVectors: !d.velocityVectors });
      return true;
    case 'KeyK':
      harness.setDebug({ checkpoints: !d.checkpoints });
      return true;
    case 'KeyG':
      harness.setDebug({ grid: !d.grid });
      return true;
    case 'KeyH':
      harness.setDebug({ debugHud: !d.debugHud });
      return true;
    case 'KeyR':
      harness.setDebug({ racingLine: !d.racingLine });
      return true;
    case 'KeyT':
      harness.setDebug({ skidmarks: !d.skidmarks });
      return true;
    case 'F1':
      harness.setDebug({ help: !d.help });
      return true;
    default:
      return false;
  }
}
